import { ldClient } from "./client";
import type { LdPickupFxWire, LdServerMessage } from "../../../lib/multiplayer/loot-drop-protocol";
import { ldWsUrl } from "../../../lib/multiplayer/loot-drop-protocol";
import {
  drawCosmeticOverlaysForIds,
  drawCursorWithCosmetic,
  loadCosmeticImage,
} from "../cursor-cosmetics";

export type LdGameHooks = {
  initGame: (mode: string) => void;
  showEl: (el: HTMLElement | null) => void;
  hideEl: (el: HTMLElement | null) => void;
  getCanvasSize: () => { w: number; h: number };
  getMouse: () => { x: number; y: number };
  setMouse: (x: number, y: number) => void;
  isPlaying: () => boolean;
  setTopHudText: (text: string) => void;
  releaseGamePointer: () => void;
  getCursorRadiusPx: () => number;
  clampCursorToEdgeInset: (x: number, y: number) => { x: number; y: number };
  drawPlayerName: (ctx: CanvasRenderingContext2D, x: number, y: number, name: string, color: string) => void;
  playModeCardBeep: () => void;
  /** Catcher-only pickup chime. */
  playLootPickupSound?: () => void;
  /** Show the OS mouse cursor (host aiming). */
  showSystemCursor?: () => void;
  getCursorColor?: () => string;
};

const HOST_CURSOR_COLOR = "#ffe566";
const NAME_COLOR = "#ffffff";

export type LdSelectedShopItem = {
  id: string;
  name: string;
  priceDots: number;
  imageData: string;
};

type RemoteInterp = {
  id: string;
  name: string;
  renderX: number;
  renderY: number;
  isHost: boolean;
  equippedCosmeticIds: string[];
  cursorColor: string;
};

type PickupAnim = LdPickupFxWire & { createdAt: number };

let hooks: LdGameHooks | null = null;
let active = false;
let posSeq = 0;
let lastPosSend = 0;
const POS_SEND_MS = 16;
const remotePlayers = new Map<string, RemoteInterp>();
const pickupAnims: PickupAnim[] = [];
const dropImageCache = new Map<string, HTMLImageElement | null>();
let selectedShopItem: LdSelectedShopItem | null = null;

/** Mobile host: hold+drag spawns drops along the path like many taps. */
const HOST_DRAG_MIN_DIST_PX = 26;
const HOST_DRAG_MIN_INTERVAL_MS = 55;
let hostPaintActive = false;
let hostPaintLastSpawnX = 0;
let hostPaintLastSpawnY = 0;
let hostPaintLastSpawnAt = 0;

export function initLootDropGameRuntime(h: LdGameHooks): void {
  hooks = h;
}

export async function ensureLdConnected(): Promise<void> {
  if (ldClient.connected && ldClient.playerId) return;
  await ldClient.connect(ldWsUrl());
}

export function isLootDropActive(): boolean {
  return active;
}

export function isLdHost(): boolean {
  return ldClient.isHost();
}

export function shouldSkipLootDropGameplay(): boolean {
  return active;
}

export function getSelectedLdShopItem(): LdSelectedShopItem | null {
  return selectedShopItem;
}

export function setSelectedLdShopItem(item: LdSelectedShopItem | null): void {
  selectedShopItem = item;
}

export function showLdHostPanel(): void {
  hooks?.showEl(document.getElementById("ldHostPanel"));
  hideLdPlayerPanel();
}

export function hideLdHostPanel(): void {
  hooks?.hideEl(document.getElementById("ldHostPanel"));
}

export function showLdPlayerPanel(): void {
  hooks?.showEl(document.getElementById("ldPlayerPanel"));
}

export function hideLdPlayerPanel(): void {
  hooks?.hideEl(document.getElementById("ldPlayerPanel"));
}

export function showLdLeaderboardPanel(): void {
  hooks?.showEl(document.getElementById("ldLeaderboardPanel"));
}

export function hideLdLeaderboardPanel(): void {
  hooks?.hideEl(document.getElementById("ldLeaderboardPanel"));
}

export function updateLdPlayerPanelBalance(balance: number): void {
  const el = document.getElementById("ldPlayerPanelBalance");
  if (el) el.textContent = `${Math.max(0, Math.floor(balance)).toLocaleString()} Dots`;
}

export function updateLdPlayerPanelHostBalance(balance: number): void {
  const el = document.getElementById("ldPlayerPanelHostBalance");
  if (el) el.textContent = `${Math.max(0, Math.floor(balance)).toLocaleString()} Dots`;
}

export function updateLdSessionLimitUi(spent: number, limit: number): void {
  const el = document.getElementById("ldHostPanelSessionLimit");
  if (!el) return;
  const s = Math.max(0, Math.floor(spent));
  const lim = Math.max(0, Math.floor(limit));
  if (lim <= 0) {
    el.textContent = "Session: No limit";
    return;
  }
  const remaining = Math.max(0, lim - s);
  el.textContent = `Session: ${remaining.toLocaleString()} / ${lim.toLocaleString()} left`;
}

export function updateLdHostPlayersList(): void {
  const ul = document.getElementById("ldHostPlayersList");
  if (!ul) return;
  ul.replaceChildren();
  const room = ldClient.room;
  if (!room || !ldClient.isHost()) return;
  for (const p of room.players) {
    if (p.isHost) continue;
    const li = document.createElement("li");
    li.className = "ldHostPlayerItem";
    const name = document.createElement("span");
    name.className = "ldHostPlayerName";
    name.textContent = p.name;
    const actions = document.createElement("div");
    actions.className = "ldHostPlayerActions";
    const kickBtn = document.createElement("button");
    kickBtn.type = "button";
    kickBtn.className = "btn";
    kickBtn.textContent = "Kick";
    kickBtn.onclick = () => {
      hooks?.playModeCardBeep();
      ldClient.kickPlayer(p.id);
    };
    const blockBtn = document.createElement("button");
    blockBtn.type = "button";
    blockBtn.className = "btn";
    blockBtn.textContent = "Block";
    blockBtn.onclick = () => {
      hooks?.playModeCardBeep();
      ldClient.blockPlayer(p.id);
    };
    actions.appendChild(kickBtn);
    actions.appendChild(blockBtn);
    li.appendChild(name);
    li.appendChild(actions);
    ul.appendChild(li);
  }
}

export function updateLdLeaderboard(): void {
  const ul = document.getElementById("ldLeaderboardList");
  if (!ul) return;
  ul.replaceChildren();
  const room = ldClient.room;
  if (!room) return;

  const earnedById = new Map<string, number>();
  for (const p of room.players) {
    if (p.isHost) continue;
    earnedById.set(p.id, Math.max(0, Math.floor(p.dotsEarned || 0)));
  }
  const session = ldClient.lastSessionState;
  if (session) {
    for (const sp of session.players) {
      if (sp.isHost) continue;
      if (typeof sp.dotsEarned === "number") {
        earnedById.set(sp.id, Math.max(0, Math.floor(sp.dotsEarned)));
      }
    }
  }

  const rows = room.players
    .filter((p) => !p.isHost)
    .map((p) => ({
      id: p.id,
      name: p.name,
      dots: earnedById.get(p.id) ?? 0,
    }))
    .sort((a, b) => b.dots - a.dots || a.name.localeCompare(b.name));

  if (!rows.length) {
    const li = document.createElement("li");
    li.className = "ldLeaderboardItem";
    li.textContent = "No players yet";
    ul.appendChild(li);
    return;
  }

  for (const row of rows) {
    const li = document.createElement("li");
    li.className = "ldLeaderboardItem";
    const name = document.createElement("span");
    name.className = "ldLeaderboardName";
    name.textContent = row.name;
    const dots = document.createElement("span");
    dots.className = "ldLeaderboardDots";
    dots.textContent = row.dots.toLocaleString();
    li.appendChild(name);
    li.appendChild(dots);
    ul.appendChild(li);
  }
}

/** Spawn a local "+X Dots" float at canvas-normalized coords (0–1). Catcher-only. */
export function spawnLocalPickupFx(amount: number, nx: number, ny: number): void {
  pickupAnims.push({
    id: `local-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    amount: Math.max(1, Math.floor(amount)),
    x: Math.max(0, Math.min(1, nx)),
    y: Math.max(0, Math.min(1, ny)),
    playerId: ldClient.playerId ?? "",
    playerName: "",
    createdAt: Date.now(),
  });
  hooks?.playLootPickupSound?.();
}

function applyHostCursorVisibility(): void {
  if (!hooks || !ldClient.isHost()) return;
  hooks.releaseGamePointer();
  hooks.showSystemCursor?.();
}

function resolveLocalCursorColor(): string {
  return hooks?.getCursorColor?.() || "#ffffff";
}

export async function startLootDropSession(): Promise<void> {
  if (!hooks) return;
  active = true;
  posSeq = 0;
  lastPosSend = 0;
  remotePlayers.clear();
  pickupAnims.length = 0;

  hooks.initGame("lootdrop");
  const { w, h } = hooks.getCanvasSize();

  if (ldClient.isHost()) {
    hooks.setTopHudText("LOOTDROP · HOST");
    // initGame hides the system cursor — restore it so the host can aim drops.
    applyHostCursorVisibility();
    // Track absolute position from the start (center until first move).
    hooks.setMouse(w / 2, h / 2);
  } else {
    const pos = hooks.clampCursorToEdgeInset(w / 2, h / 2);
    hooks.setMouse(pos.x, pos.y);
    hooks.setTopHudText("LOOTDROP");
    sendPosNow();
  }

  syncRemoteFromRoom();
  updateLdLeaderboard();
  showLdLeaderboardPanel();
}

export function stopLootDropSession(): void {
  active = false;
  endLootDropHostPaint();
  remotePlayers.clear();
  pickupAnims.length = 0;
  selectedShopItem = null;
  hideLdHostPanel();
  hideLdPlayerPanel();
  hideLdLeaderboardPanel();
  hooks?.releaseGamePointer();
}

export function releaseLootDropPointer(): void {
  hooks?.releaseGamePointer();
}

function normFromCanvas(x: number, y: number, cw: number, ch: number): { x: number; y: number } {
  return { x: x / Math.max(1, cw), y: y / Math.max(1, ch) };
}

function canvasFromNorm(nx: number, ny: number, cw: number, ch: number): { x: number; y: number } {
  return { x: nx * cw, y: ny * ch };
}

function clientToCanvasXY(
  clientX: number,
  clientY: number,
  canvas: HTMLCanvasElement,
): { x: number; y: number } {
  const rect = canvas.getBoundingClientRect();
  const scaleX = canvas.width / Math.max(1, rect.width);
  const scaleY = canvas.height / Math.max(1, rect.height);
  return {
    x: (clientX - rect.left) * scaleX,
    y: (clientY - rect.top) * scaleY,
  };
}

function sendPosNow(): void {
  if (!hooks || ldClient.isHost()) return;
  const { w, h } = hooks.getCanvasSize();
  const mouse = hooks.getMouse();
  const norm = normFromCanvas(mouse.x, mouse.y, w, h);
  ldClient.sendPos(norm.x, norm.y, posSeq++);
  lastPosSend = performance.now();
}

/** Absolute mouse tracking for the host (and guests on pointermove when needed). */
export function updateLootDropPointerFromClient(
  clientX: number,
  clientY: number,
  canvas: HTMLCanvasElement,
): void {
  if (!active || !hooks) return;
  const { x, y } = clientToCanvasXY(clientX, clientY, canvas);
  if (ldClient.isHost()) {
    hooks.setMouse(x, y);
    applyHostCursorVisibility();
  }
}

export function tickLootDrop(dt: number): void {
  if (!active || !hooks) return;

  if (ldClient.isHost()) {
    applyHostCursorVisibility();
  } else {
    const mouse = hooks.getMouse();
    const clamped = hooks.clampCursorToEdgeInset(mouse.x, mouse.y);
    if (clamped.x !== mouse.x || clamped.y !== mouse.y) {
      hooks.setMouse(clamped.x, clamped.y);
    }
    const now = performance.now();
    if (now - lastPosSend >= POS_SEND_MS) {
      sendPosNow();
    }
  }

  const fs = Math.min(3, dt * 60);
  const state = ldClient.lastSessionState;
  if (state) {
    const seen = new Set<string>();
    for (const sp of state.players) {
      if (sp.id === ldClient.playerId) continue;
      if (sp.isHost) continue;
      seen.add(sp.id);
      let rp = remotePlayers.get(sp.id);
      if (!rp) {
        const roomP = ldClient.room?.players.find((p) => p.id === sp.id);
        rp = {
          id: sp.id,
          name: roomP?.name ?? "Player",
          renderX: sp.x,
          renderY: sp.y,
          isHost: sp.isHost,
          equippedCosmeticIds: roomP?.equippedCosmeticIds ?? [],
          cursorColor: roomP?.cursorColor || "#ffffff",
        };
        remotePlayers.set(sp.id, rp);
      }
      rp.renderX += (sp.x - rp.renderX) * 0.35 * fs;
      rp.renderY += (sp.y - rp.renderY) * 0.35 * fs;
    }
    // Remove cursors for players who left the LootDrop.
    for (const id of [...remotePlayers.keys()]) {
      if (!seen.has(id)) remotePlayers.delete(id);
    }
  }

  const wallNow = Date.now();
  for (let i = pickupAnims.length - 1; i >= 0; i--) {
    if (wallNow - pickupAnims[i]!.createdAt > 1400) pickupAnims.splice(i, 1);
  }
}

export function handleLdServerEvent(msg: LdServerMessage): void {
  if (msg.type === "room_state") {
    pruneRemotePlayersToRoom();
    updateLdHostPlayersList();
    updateLdLeaderboard();
  }
  if (msg.type === "session_state") {
    pruneRemotePlayersToIds(msg.state.players.map((p) => p.id));
    updateLdLeaderboard();
    for (const fx of msg.state.pickupFx) {
      // Only the catcher sees the floating pickup text and hears the sound.
      if (fx.playerId !== ldClient.playerId) continue;
      if (!pickupAnims.some((a) => a.id === fx.id)) {
        pickupAnims.push({ ...fx, createdAt: Date.now() });
        hooks?.playLootPickupSound?.();
      }
    }
  }
}

function pruneRemotePlayersToIds(ids: string[]): void {
  const keep = new Set(ids.filter((id) => id !== ldClient.playerId));
  for (const id of [...remotePlayers.keys()]) {
    if (!keep.has(id)) remotePlayers.delete(id);
  }
}

function pruneRemotePlayersToRoom(): void {
  if (!ldClient.room) {
    remotePlayers.clear();
    return;
  }
  pruneRemotePlayersToIds(ldClient.room.players.map((p) => p.id));
}

/** Call when room membership changes so departed cursors disappear immediately. */
export function syncLootDropRemotesFromRoom(): void {
  pruneRemotePlayersToRoom();
  if (!ldClient.room) return;
  for (const p of ldClient.room.players) {
    if (p.id === ldClient.playerId || p.isHost) continue;
    const existing = remotePlayers.get(p.id);
    if (existing) {
      existing.name = p.name;
      existing.equippedCosmeticIds = p.equippedCosmeticIds ?? [];
      existing.cursorColor = p.cursorColor || "#ffffff";
      continue;
    }
    remotePlayers.set(p.id, {
      id: p.id,
      name: p.name,
      renderX: p.x,
      renderY: p.y,
      isHost: p.isHost,
      equippedCosmeticIds: p.equippedCosmeticIds ?? [],
      cursorColor: p.cursorColor || "#ffffff",
    });
  }
}

function syncRemoteFromRoom(): void {
  remotePlayers.clear();
  if (!ldClient.room) return;
  for (const p of ldClient.room.players) {
    if (p.id === ldClient.playerId || p.isHost) continue;
    remotePlayers.set(p.id, {
      id: p.id,
      name: p.name,
      renderX: p.x,
      renderY: p.y,
      isHost: p.isHost,
      equippedCosmeticIds: p.equippedCosmeticIds ?? [],
      cursorColor: p.cursorColor || "#ffffff",
    });
  }
}

export function handleLootDropCanvasClick(clientX: number, clientY: number, canvas: HTMLCanvasElement): boolean {
  if (!active || !ldClient.isHost() || !hooks) return false;
  const { x, y } = clientToCanvasXY(clientX, clientY, canvas);
  spawnHostDropAtCanvas(x, y);
  return true;
}

function spawnHostDropAtCanvas(x: number, y: number): void {
  if (!hooks || !ldClient.isHost()) return;
  hooks.setMouse(x, y);
  const { w, h } = hooks.getCanvasSize();
  const norm = normFromCanvas(x, y, w, h);
  const selected = selectedShopItem;
  if (selected) {
    ldClient.spawnDrop(selected.priceDots, norm.x, norm.y, {
      cosmeticId: selected.id,
      cosmeticName: selected.name,
      cosmeticImageData: selected.imageData,
    });
  } else {
    const amountInput = document.getElementById("ldDropAmountInput") as HTMLInputElement | null;
    const amount = Math.max(1, Math.floor(Number(amountInput?.value) || 1));
    ldClient.spawnDrop(amount, norm.x, norm.y);
  }
  hooks.playModeCardBeep();
  hostPaintLastSpawnX = x;
  hostPaintLastSpawnY = y;
  hostPaintLastSpawnAt = performance.now();
}

/** Start a mobile host paint stroke (first tap counts as one press). */
export function beginLootDropHostPaint(clientX: number, clientY: number, canvas: HTMLCanvasElement): boolean {
  if (!active || !ldClient.isHost() || !hooks) return false;
  const { x, y } = clientToCanvasXY(clientX, clientY, canvas);
  hostPaintActive = true;
  spawnHostDropAtCanvas(x, y);
  applyHostCursorVisibility();
  return true;
}

/** While finger is held, dragging spawns more drops like repeated presses. */
export function continueLootDropHostPaint(clientX: number, clientY: number, canvas: HTMLCanvasElement): void {
  if (!hostPaintActive || !active || !ldClient.isHost() || !hooks) return;
  const { x, y } = clientToCanvasXY(clientX, clientY, canvas);
  hooks.setMouse(x, y);

  let curX = hostPaintLastSpawnX;
  let curY = hostPaintLastSpawnY;
  let dx = x - curX;
  let dy = y - curY;
  let remaining = Math.hypot(dx, dy);
  if (remaining < HOST_DRAG_MIN_DIST_PX) return;

  const inv = 1 / remaining;
  const ux = dx * inv;
  const uy = dy * inv;
  let spawned = 0;
  while (remaining >= HOST_DRAG_MIN_DIST_PX && spawned < 8) {
    if (performance.now() - hostPaintLastSpawnAt < HOST_DRAG_MIN_INTERVAL_MS) break;
    curX += ux * HOST_DRAG_MIN_DIST_PX;
    curY += uy * HOST_DRAG_MIN_DIST_PX;
    remaining -= HOST_DRAG_MIN_DIST_PX;
    spawnHostDropAtCanvas(curX, curY);
    spawned++;
  }
}

export function endLootDropHostPaint(): void {
  hostPaintActive = false;
}

export function isLootDropHostPaintActive(): boolean {
  return hostPaintActive;
}

function ensureDropImage(cosmeticId: string, imageData?: string): HTMLImageElement | null {
  if (dropImageCache.has(cosmeticId)) return dropImageCache.get(cosmeticId) ?? null;
  dropImageCache.set(cosmeticId, null);
  if (!imageData) return null;
  void loadCosmeticImage(imageData).then((img) => {
    dropImageCache.set(cosmeticId, img);
  });
  return null;
}

export function renderLootDropOverlay(ctx: CanvasRenderingContext2D, cw: number, ch: number, now: number): void {
  if (!active) return;
  void now;

  const state = ldClient.lastSessionState;
  const drops = state?.drops ?? ldClient.room?.drops ?? [];

  for (const drop of drops) {
    const { x, y } = canvasFromNorm(drop.x, drop.y, cw, ch);
    // Same scale as Dots drops (glow uses r*1.8; cursor fill matches that visual size).
    const r = Math.max(14, cw * 0.022);
    if (drop.cosmeticId) {
      const img = ensureDropImage(drop.cosmeticId, drop.cosmeticImageData);
      const cursorR = r * 1.15;
      // Soft glow behind the cursor ball so it reads at the same scale as Dots.
      const grd = ctx.createRadialGradient(x, y, 0, x, y, r * 1.8);
      grd.addColorStop(0, "rgba(255, 240, 120, 0.35)");
      grd.addColorStop(0.55, "rgba(255, 200, 40, 0.18)");
      grd.addColorStop(1, "rgba(255, 160, 0, 0)");
      ctx.save();
      ctx.fillStyle = grd;
      ctx.beginPath();
      ctx.arc(x, y, r * 1.8, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
      drawCursorWithCosmetic(ctx, x, y, cursorR, "#ffffff", img, { showBase: true });
      continue;
    }

    const grd = ctx.createRadialGradient(x, y, 0, x, y, r * 1.8);
    grd.addColorStop(0, "rgba(255, 240, 120, 1)");
    grd.addColorStop(0.45, "rgba(255, 200, 40, 0.85)");
    grd.addColorStop(1, "rgba(255, 160, 0, 0)");
    ctx.save();
    ctx.fillStyle = grd;
    ctx.beginPath();
    ctx.arc(x, y, r * 1.8, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = "#fff8c0";
    ctx.beginPath();
    ctx.arc(x, y, r * 0.55, 0, Math.PI * 2);
    ctx.fill();
    ctx.font = `bold ${Math.max(11, r * 0.7)}px monospace`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillStyle = "#1a1000";
    ctx.fillText(String(drop.amount), x, y);
    ctx.restore();
  }

  for (const rp of remotePlayers.values()) {
    if (rp.isHost) continue;
    const { x, y } = canvasFromNorm(rp.renderX, rp.renderY, cw, ch);
    const color = rp.cursorColor || "#ffffff";
    drawLootCursor(ctx, x, y, color, hooks?.getCursorRadiusPx() ?? 12);
    drawCosmeticOverlaysForIds(ctx, x, y, hooks?.getCursorRadiusPx() ?? 12, rp.equippedCosmeticIds);
    hooks?.drawPlayerName(ctx, x, y, rp.name, NAME_COLOR);
  }

  if (hooks) {
    const mouse = hooks.getMouse();
    if (ldClient.isHost()) {
      // Host aiming cursor (also keep OS cursor visible).
      drawHostAimCursor(ctx, mouse.x, mouse.y, hooks.getCursorRadiusPx());
    } else {
      const color = resolveLocalCursorColor();
      drawLootCursor(ctx, mouse.x, mouse.y, color, hooks.getCursorRadiusPx());
      const me = ldClient.room?.players.find((p) => p.id === ldClient.playerId);
      const cosmetics = me?.equippedCosmeticIds ?? [];
      drawCosmeticOverlaysForIds(ctx, mouse.x, mouse.y, hooks.getCursorRadiusPx(), cosmetics);
      hooks.drawPlayerName(ctx, mouse.x, mouse.y, me?.name ?? "You", NAME_COLOR);
    }
  }

  for (const fx of pickupAnims) {
    // createdAt is wall-clock Date.now(); do not use rAF performance.now() here.
    const age = Date.now() - fx.createdAt;
    if (age < 0 || age > 1400) continue;
    const t = Math.min(1, age / 1400);
    const { x, y } = canvasFromNorm(fx.x, fx.y, cw, ch);
    const floatY = y - t * 48;
    const alpha = Math.max(0, 1 - t);
    ctx.save();
    ctx.globalAlpha = alpha;
    const fontPx = Math.max(14, Math.round(cw * 0.02));
    ctx.font = `bold ${fontPx}px "Press Start 2P", monospace`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.lineWidth = Math.max(3, fontPx * 0.18);
    ctx.strokeStyle = "rgba(0,0,0,0.75)";
    ctx.fillStyle = "#ffe566";
    const label = fx.cosmeticName
      ? `¡Obtuviste ${fx.cosmeticName}!`
      : `+${fx.amount} Dots`;
    ctx.strokeText(label, x, floatY);
    ctx.fillText(label, x, floatY);
    ctx.restore();
  }
}

function drawLootCursor(ctx: CanvasRenderingContext2D, x: number, y: number, color: string, r: number): void {
  ctx.save();
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.arc(x, y, r, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = "rgba(255,255,255,0.65)";
  ctx.lineWidth = Math.max(1, r * 0.12);
  ctx.stroke();
  ctx.restore();
}

function drawHostAimCursor(ctx: CanvasRenderingContext2D, x: number, y: number, r: number): void {
  const size = Math.max(10, r * 0.85);
  ctx.save();
  ctx.strokeStyle = HOST_CURSOR_COLOR;
  ctx.fillStyle = "rgba(255, 229, 102, 0.25)";
  ctx.lineWidth = Math.max(2, size * 0.18);
  ctx.beginPath();
  ctx.arc(x, y, size, 0, Math.PI * 2);
  ctx.fill();
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(x - size * 1.4, y);
  ctx.lineTo(x + size * 1.4, y);
  ctx.moveTo(x, y - size * 1.4);
  ctx.lineTo(x, y + size * 1.4);
  ctx.stroke();
  ctx.restore();
}
